import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import StreamPageHeader from './partial/stream/StreamPageHeader.jsx';
import LanguageSelector from './partial/LanguageSelector.jsx';
import PhotosensitivityToggle from './partial/stream/PhotosensitivityToggle.jsx';
import styles from '../styles/ProfilePage.module.css';
import '../styles/ProfilePage.css';
import config from '../config.js';
import { FaGoogle, FaTwitch, FaApple } from 'react-icons/fa';

import { useFetchWrapper } from '../_helpers/fetchWrapper.js';
import { clearOAuthFlowState, clearStaleFlowStorage, markFlowStarted } from '../_helpers/oauthFlowState.js';
import { useLocale } from '../contexts/LocaleContext.jsx';
import { PHOTOSENSITIVITY_STATUS, useAccountFxPreferences } from '../contexts/AccountFxPreferences.jsx';

/** The Photosensitivity mode toggle's anchor: the warning's link lands on `/profile#photosensitivity` (ENG-836). */
export const PHOTOSENSITIVITY_ANCHOR = 'photosensitivity';

/** The providers a listener may link, in display order, with their catalog names. */
const PROVIDERS = ['google', 'twitch', 'apple'];
const PROVIDER_NAME_KEYS = {
  google: 'profile.provider.google',
  twitch: 'profile.provider.twitch',
  apple: 'profile.provider.apple',
};

/** A provider id as its first letter uppercased ("Google"), for a provider this build has no catalog name for. */
const capitalize = (provider) => provider.charAt(0).toUpperCase() + provider.slice(1);

function ProfilePage() {
  const [username, setUsername] = useState('');
  const [bio, setBio] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { t, format } = useLocale();
  const { status: photosensitivityStatus } = useAccountFxPreferences();
  const photosensitivitySwitchRef = useRef(null);
  // The location the toggle was last focused for, so it is focused once per arrival.
  const focusedForRef = useRef(null);

  const [homePage, setHomePage] = useState('');
  const [homePages, setHomePages] = useState([]);

  const [linkedAccounts, setLinkedAccounts] = useState({
    google: null,
    twitch: null,
    apple: null
  });
  const [linkingInProgress, setLinkingInProgress] = useState({});
  // The alert under the social card: a catalog descriptor `{ key, params }`
  // (`{ key, providerId }` when the message names a provider, resolved to its
  // display name at render), or `{ text }` for a message the backend itself
  // worded; null when hidden.
  const [linkingMessage, setLinkingMessage] = useState(null);
  const [linkingMessageType, setLinkingMessageType] = useState('success');

  const fetchWrapperRef = useRef(useFetchWrapper());
  const dismissTimerRef = useRef(null);

  useEffect(() => {
    document.body.classList.add('profile-page-body');
    return () => {
      document.body.classList.remove('profile-page-body');
      clearTimeout(dismissTimerRef.current);
    };
  }, []);

  // Arriving at /profile#photosensitivity (the warning's "Go to Photosensitivity
  // mode", ENG-836): scroll the toggle into view and focus it, once it can take
  // focus (it is disabled until the account has loaded).
  useEffect(() => {
    if (location.hash !== `#${PHOTOSENSITIVITY_ANCHOR}`) return;
    if (photosensitivityStatus !== PHOTOSENSITIVITY_STATUS.READY) return;
    if (focusedForRef.current === location.key) return;
    const target = photosensitivitySwitchRef.current;
    if (!target) return;
    focusedForRef.current = location.key;
    document.getElementById(PHOTOSENSITIVITY_ANCHOR)?.scrollIntoView?.({ block: 'center' });
    target.focus();
  }, [location.hash, location.key, photosensitivityStatus]);

  const scheduleDismiss = useCallback(() => {
    clearTimeout(dismissTimerRef.current);
    dismissTimerRef.current = setTimeout(() => setLinkingMessage(null), 5000);
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const linked = urlParams.get('linked');
    const error = urlParams.get('error');

    if (linked === 'success') {
      const provider = urlParams.get('provider');
      setLinkingMessage(
        provider
          ? { key: 'profile.messages.linkedSuccess', providerId: provider }
          : { key: 'profile.messages.linkedSuccessGeneric' }
      );
      setLinkingMessageType('success');
      fetchLinkedAccounts();
    } else if (linked === 'error' || error) {
      const errorMsg = decodeURIComponent(error || 'Failed to link account');
      if (errorMsg && errorMsg.includes('already linked to another user')) {
        setLinkingMessage({ key: 'profile.messages.alreadyLinkedToAnotherUser' });
      } else if (errorMsg && errorMsg.includes('already linked to this user')) {
        setLinkingMessage({ key: 'profile.messages.alreadyLinkedToYou' });
      } else if (errorMsg && errorMsg.includes('Authorization failed')) {
        setLinkingMessage({ key: 'profile.messages.authorizationFailed' });
      } else if (errorMsg && errorMsg.includes('Invalid authentication token')) {
        setLinkingMessage({ key: 'profile.messages.invalidToken' });
      } else {
        setLinkingMessage({ key: 'profile.messages.linkFailed' });
      }
      setLinkingMessageType('error');
    }

    if (linked || error) {
      const url = new URL(window.location);
      url.searchParams.delete('linked');
      url.searchParams.delete('error');
      // The provider only names the account in the message; left behind it
      // outlived the message on the address (ENG-534).
      url.searchParams.delete('provider');
      window.history.replaceState({}, '', url);
      scheduleDismiss();
    }
  }, [scheduleDismiss]);

  const fetchLinkedAccounts = async () => {
    try {
      const socialAccountsData = await fetchWrapperRef.current.get(`${config.apiBaseUrl}/user/social/accounts`);

      const linkedAccountsObj = {
        google: null,
        twitch: null,
        apple: null
      };

      socialAccountsData.forEach(account => {
        if (Object.prototype.hasOwnProperty.call(linkedAccountsObj, account.provider)) {
          linkedAccountsObj[account.provider] = {
            provider: account.provider,
            email: account.email,
            linked_at: account.linked_at
          };
        }
      });

      setLinkedAccounts(linkedAccountsObj);
    } catch (error) {
      console.error('Failed to fetch linked accounts:', error);
    }
  };

  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        // Never ask for `/user/profile/null` (ENG-499): RequireAuth keeps
        // signed-out visitors off this page, and a session without a user id
        // has no profile to load.
        const userId = sessionStorage.getItem('userId');
        if (!userId) return;
        const data = await fetchWrapperRef.current.get(`${config.apiBaseUrl}/user/profile/${userId}`);

        setUsername(data.username || '');
        setBio(data.bio || '');
        setHomePage(data.default_login_page || '');

        await fetchLinkedAccounts();
      } catch (error) {
        console.error('Failed to fetch profile data:', error);
        setLinkingMessage({ key: 'profile.messages.loadFailed' });
        setLinkingMessageType('error');
      }
    };

    fetchProfileData();
  }, []);

  useEffect(() => {
    const fetchHomePages = async () => {
      try {
        const data = await fetchWrapperRef.current.get(`${config.apiBaseUrl}/user/homepageoptions`);
        setHomePages(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Failed to fetch home page options:', error);
        setHomePages([]);
      }
    };

    fetchHomePages();
  }, []);

  // A saved home page that is not among the options (or none at all) shows
  // as "Not set", so that is what Save writes (PROF-01, ENG-523): the select
  // used to show the first option while the old value went back. With no
  // options loaded there is nothing to compare against, so it is kept.
  const shownHomePage =
    homePages.length > 0 && !homePages.some((page) => page.default_login_page === homePage) ? '' : homePage;

  const handleSave = async () => {
    try {
      await fetchWrapperRef.current.put(`${config.apiBaseUrl}/user`,
        { username, bio, default_login_page: shownHomePage });
      navigate('/home');
    } catch (error) {
      console.error('Failed to save profile:', error);
      setLinkingMessage({ key: 'profile.messages.saveFailed' });
      setLinkingMessageType('error');
    }
  };

  const handleHomePageChange = (e) => {
    setHomePage(e.target.value);
  };

  const handleLinkAccount = async (provider) => {
    const userId = sessionStorage.getItem('userId');

    if (!userId) {
      console.error('userId not found');
      setLinkingMessage({ key: 'profile.messages.authenticationRequired' });
      setLinkingMessageType('error');
      return;
    }

    setLinkingInProgress(prev => ({ ...prev, [provider]: true }));

    try {
      // This linking attempt is the round-trip now in flight, so whatever an
      // earlier one — or a login the user walked away from — left behind goes
      // first: OAuthPage takes any stored provider as proof that a callback is
      // expected, and only one flow can be expected at a time.
      clearStaleFlowStorage();
      localStorage.setItem('linking-provider', provider);
      localStorage.setItem('linking-mode', 'true');
      // Stamped like the login hand-off, so an abandoned linking attempt stops
      // standing in for one in progress once it ages out (FLOW_MAX_AGE_MS).
      markFlowStarted();

      // The backend mints the OAuth state and PKCE pair and binds them to
      // this session (ENG-472). What comes back is a URL on the API carrying a
      // single-use, 30-second link ticket; the JWT itself only travels in the
      // Authorization header (added by fetchWrapper), never in a URL. The
      // browser goes there rather than straight to the provider because
      // Apple's flow sets a cookie on the way that binds the link to it.
      const { oauth_url } = await fetchWrapperRef.current.post(
        `${config.apiBaseUrl}/auth/${provider}/link/initiate`
      );
      if (!oauth_url) throw new Error('No link URL received');
      window.location.href = oauth_url;
    } catch (error) {
      console.error('Failed to initiate account linking:', error);
      // The linking keys were written before the request; without this they
      // would outlive the failed attempt and hijack the user's next login
      clearOAuthFlowState();
      setLinkingInProgress(prev => ({ ...prev, [provider]: false }));
      setLinkingMessage({ key: 'profile.messages.linkInitiateFailed' });
      setLinkingMessageType('error');
    }
  };

  const handleUnlinkAccount = async (provider) => {
    try {
      const response = await fetchWrapperRef.current.delete(`${config.apiBaseUrl}/user/social/unlink/${provider}`);

      setLinkedAccounts(prev => ({
        ...prev,
        [provider]: null
      }));

      setLinkingMessage(
        response.message
          ? { text: response.message }
          : { key: 'profile.messages.unlinkedSuccess', providerId: provider }
      );
      setLinkingMessageType('success');
      scheduleDismiss();
    } catch (error) {
      console.error(`Failed to unlink ${provider} account:`, error);
      setLinkingMessage({ key: 'profile.messages.unlinkFailed', providerId: provider });
      setLinkingMessageType('error');
      scheduleDismiss();
    }
  };

  const getSocialIcon = (provider) => {
    switch (provider) {
      case 'twitch': return FaTwitch;
      case 'apple': return FaApple;
      default: return FaGoogle;
    }
  };

  const getLinkedAccountsCount = () =>
    Object.values(linkedAccounts).filter(account => account !== null).length;

  const isLastLinkedAccount = () => getLinkedAccountsCount() === 1;

  /** The provider's display name in the active locale ("Google"). */
  const providerName = (provider) => (PROVIDER_NAME_KEYS[provider] ? t(PROVIDER_NAME_KEYS[provider]) : capitalize(provider));

  const alertText = (message) => {
    if (!message.key) return message.text;
    const params = message.providerId ? { ...message.params, provider: providerName(message.providerId) } : message.params;
    return t(message.key, params);
  };

  const getAlertClass = (type) => {
    if (type === 'error') return `${styles.alert} ${styles.alertError}`;
    return `${styles.alert} ${styles.alertSuccess}`;
  };

  return (
    <div className="profile-page">
      <StreamPageHeader />

      <div className="profile-page-main">
        <div className="profile-card">
          <h2 className={styles.title}>{t('profile.title')}</h2>

          <div className={styles.form}>
            <div className={styles.fieldGroup}>
              <label htmlFor="profile-username" className={styles.label}>{t('profile.form.usernameLabel')}</label>
              <input
                id="profile-username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className={styles.input}
                placeholder={t('profile.form.usernamePlaceholder')}
                maxLength={50}
                autoComplete="username"
                name="username"
              />
            </div>

            <div className={styles.fieldGroup}>
              <label htmlFor="profile-bio" className={styles.label}>{t('profile.form.bioLabel')}</label>
              <textarea
                id="profile-bio"
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                className={styles.textarea}
                placeholder={t('profile.form.bioPlaceholder')}
                maxLength={500}
                autoComplete="off"
                name="bio"
              />
            </div>

            <div className={styles.fieldGroup}>
              <label htmlFor="profile-homepage" className={styles.label}>{t('profile.form.homePageLabel')}</label>
              <select
                id="profile-homepage"
                className={styles.select}
                value={shownHomePage}
                onChange={handleHomePageChange}
              >
                {homePages && homePages.length > 0 ? (
                  <>
                    <option value="">{t('profile.form.homePageNotSet')}</option>
                    {homePages.map((page) => (
                      <option key={page.id} value={page.default_login_page}>
                        {page.friendly_name}
                      </option>
                    ))}
                  </>
                ) : (
                  <option value="">{t('profile.form.homePageLoading')}</option>
                )}
              </select>
            </div>

            <div className={styles.formNav}>
              <button
                className={styles.btnCancel}
                onClick={() => navigate('/home')}
              >
                {t('common.actions.cancel')}
              </button>
              <button
                className={styles.btnSave}
                onClick={handleSave}
              >
                {t('common.actions.save')}
              </button>
            </div>
          </div>

          {/* Preferences apply on change (ENG-380), so they sit in their own
              section outside the Save/Cancel form above. It always renders
              (ENG-836): Photosensitivity mode is always there, and only the
              language selector waits for a second locale (it renders nothing
              until then). */}
          <section className={styles.preferences} aria-labelledby="profile-preferences-title">
            <h3 id="profile-preferences-title" className={styles.preferencesTitle}>{t('profile.preferences.title')}</h3>
            <p className={styles.preferencesDescription}>{t('profile.preferences.description')}</p>
            <div id={PHOTOSENSITIVITY_ANCHOR} className={styles.fieldGroup}>
              <PhotosensitivityToggle
                variant="profile"
                id="profile-photosensitivity"
                switchRef={photosensitivitySwitchRef}
                classNames={{ hint: styles.hint, status: styles.status, retry: styles.btnLink }}
              />
            </div>
            <LanguageSelector
              id="profile-language"
              classNames={{ root: styles.fieldGroup, label: styles.label, select: styles.select, hint: styles.hint, status: styles.status, retry: styles.btnLink }}
            />
          </section>
        </div>

        <div className="profile-social-card">
          <h3 className={styles.socialTitle}>{t('profile.social.title')}</h3>
          <p className={styles.socialDescription}>
            {t('profile.social.description')}
          </p>

          {linkingMessage && (
            <div className={getAlertClass(linkingMessageType)} role="alert">
              {alertText(linkingMessage)}
            </div>
          )}

          {PROVIDERS.map(provider => {
            const IconComponent = getSocialIcon(provider);
            const isLinked = linkedAccounts[provider];
            const isLinking = linkingInProgress[provider];

            return (
              <div
                key={provider}
                className={styles.socialItem}
                data-testid={isLinked ? 'social-item-linked' : undefined}
              >
                <div className={styles.socialInfo}>
                  <div className={styles.socialIcon}>
                    <IconComponent aria-hidden="true" />
                  </div>
                  <div className={styles.socialDetails}>
                    <span className={styles.socialName}>
                      {providerName(provider)}
                    </span>
                    {isLinked && (
                      <>
                        <span className={styles.socialConnected}>{t('profile.social.connected')}</span>
                        {isLinked.email ? (
                          <span className={styles.socialEmail}>{isLinked.email}</span>
                        ) : format.date(isLinked.linked_at) ? (
                          // An unparseable linked_at drops the line rather than reading "Since ".
                          <span className={styles.socialEmail}>{t('profile.social.connectedSince', { date: format.date(isLinked.linked_at) })}</span>
                        ) : null}
                      </>
                    )}
                  </div>
                </div>

                <div className={styles.socialActions}>
                  {isLinked ? (
                    <button
                      onClick={() => handleUnlinkAccount(provider)}
                      className={styles.btnUnlink}
                      disabled={isLinking || isLastLinkedAccount()}
                      title={isLastLinkedAccount() ? t('profile.social.cannotUnlinkOnlyLogin') : ''}
                      aria-label={t('profile.social.unlinkAccountLabel', { provider: providerName(provider) })}
                    >
                      {t('profile.social.unlink')}
                    </button>
                  ) : (
                    <button
                      onClick={() => handleLinkAccount(provider)}
                      className={styles.btnLink}
                      disabled={isLinking}
                      aria-label={t('profile.social.linkAccountLabel', { provider: providerName(provider) })}
                    >
                      {isLinking ? t('profile.social.linking') : t('profile.social.linkAccount')}
                    </button>
                  )}
                </div>
              </div>
            );
          })}

          {isLastLinkedAccount() && (
            <div className={styles.warningBox} role="alert">
              {t('profile.social.lastAccountWarning')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProfilePage;
